import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Typography,
  Box,
  IconButton,
  Grid,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { getAllAdmins, changeBrokerManager } from '../../../redux/slices/operator';
import { Wrapper } from '../../../sections/@dashboard/Trade/watchlist/BuyScriptDrawer';
import useAuth from '../../../hooks/useAuth';

const ChangeAdminModal = ({ broker, open, onClose, onUserUpdate }) => {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingAdmins, setIsFetchingAdmins] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [selectedManagerId, setSelectedManagerId] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  const isMountedRef = useRef(true);

  // Get broker's current manager ID
  const currentManagerId = broker?.manager?._id;

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setAdmins([]);
      setSelectedManagerId('');
      setIsFetchingAdmins(false);
      setIsLoading(false);
    }
  }, [open]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!open || !broker?._id || !user?._id) return;

    let isCancelled = false;

    const fetchAdmins = async () => {
      try {
        setIsFetchingAdmins(true);
        // Pass current logged-in user's ID as managerId
        const result = await dispatch(getAllAdmins(user._id));
        
        if (isCancelled || !isMountedRef.current) return;

        if (result?.success && result?.data) {
          // Handle response structure similar to getOperator - expects docs array
          const adminData = result.data.docs || result.data || [];
          
          // First, find the current manager in the admin list to get their profitLossType
          const currentManager = currentManagerId
            ? adminData.find((admin) => admin._id === currentManagerId)
            : null;
          
          // Get the profitLossType from the current manager's userConfig
          const managerProfitLossType = currentManager?.userConfig?.profitLossType;
          
          // Filter admins by userConfig.profitLossType matching the current manager's profitLossType
          const filteredAdmins = managerProfitLossType
            ? adminData.filter((admin) => admin?.userConfig?.profitLossType === managerProfitLossType)
            : adminData;
          
          if (isMountedRef.current && !isCancelled) {
            setAdmins(filteredAdmins);
          }
        } else if (isMountedRef.current && !isCancelled) {
          enqueueSnackbar(result?.error || 'Failed to fetch admins', { variant: 'error' });
        }
      } catch (err) {
        console.error('Error fetching admins:', err);
        if (isMountedRef.current && !isCancelled) {
          enqueueSnackbar('Failed to fetch admins', { variant: 'error' });
        }
      } finally {
        if (isMountedRef.current && !isCancelled) {
          setIsFetchingAdmins(false);
        }
      }
    };

    fetchAdmins();
    
    // Set current manager as selected
    if (broker?.manager?._id) {
      setSelectedManagerId(broker.manager._id);
    } else {
      setSelectedManagerId('');
    }

    return () => {
      isCancelled = true;
    };
  }, [open, broker?._id, broker?.manager?._id, currentManagerId, user?._id, dispatch, enqueueSnackbar]);

  const handleChangeManager = async () => {
    if (!selectedManagerId) {
      enqueueSnackbar('Please select an admin', { variant: 'warning' });
      return;
    }

    try {
      setIsLoading(true);
      const result = await dispatch(changeBrokerManager(broker._id, selectedManagerId));
      if (result?.success) {
        const message = result?.data?.message || 'Manager changed successfully';
        enqueueSnackbar(message, { variant: 'success' });
        setIsLoading(false);
        if (typeof onUserUpdate === 'function') {
          onUserUpdate();
        }
        onClose();
      } else {
        setIsLoading(false);
        enqueueSnackbar(result?.error || 'Failed to change manager', { variant: 'error' });
      }
    } catch (err) {
      setIsLoading(false);
      enqueueSnackbar(err?.message || 'Failed to change manager', { variant: 'error' });
    }
  };

  return (
    <Wrapper
      open={open}
      onClose={onClose}
      children={
        <Box width={{ xs: '100%', sm: '400px' }} marginX={'auto'}>
          <IconButton sx={{ position: 'absolute', top: 0, right: 0 }} onClick={onClose}>
            <CloseIcon />
          </IconButton>
          <Typography variant="h4" textAlign="center" pb={2}>
            Change Admin
          </Typography>
          {isFetchingAdmins ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={3}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              <Typography variant="body1" textAlign="center" pb={2}>
                Select a new admin for this broker
              </Typography>
              {admins.length > 0 && admins[0]?.userConfig?.profitLossType && (
                <Typography variant="body2" textAlign="center" pb={2} color="text.secondary">
                  Showing admins with profit/loss type: {admins[0].userConfig.profitLossType}
                </Typography>
              )}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="admin-select-label">Select Admin</InputLabel>
                <Select
                  labelId="admin-select-label"
                  id="admin-select"
                  value={selectedManagerId}
                  label="Select Admin"
                  onChange={(e) => setSelectedManagerId(e.target.value)}
                >
                  {admins.map((admin) => (
                    <MenuItem key={admin._id} value={admin._id}>
                      {admin.username} {admin.firstname && admin.lastname ? `(${admin.firstname} ${admin.lastname})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {admins.length === 0 && (
                <Typography variant="body2" textAlign="center" pb={2} color="warning.main">
                  No admins found with matching profit/loss type
                </Typography>
              )}
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={6}>
                  <LoadingButton
                    variant="contained"
                    fullWidth
                    size="large"
                    onClick={handleChangeManager}
                    color="primary"
                    loading={isLoading}
                    disabled={!selectedManagerId || admins.length === 0}
                  >
                    Change
                  </LoadingButton>
                </Grid>
                <Grid item xs={6}>
                  <Button variant="outlined" fullWidth size="large" onClick={onClose}>
                    Cancel
                  </Button>
                </Grid>
              </Grid>
            </>
          )}
        </Box>
      }
    />
  );
};

ChangeAdminModal.propTypes = {
  broker: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUserUpdate: PropTypes.func,
};

export default ChangeAdminModal;

